import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Points,
  ShaderMaterial,
  Uniform,
} from "three";
import {
  DEFAULT_UNDERWATER_VISUAL_SETTINGS,
  type UnderwaterVisualSettings,
} from "./VisualSettings";

type ParticleSeed = {
  baseX: number;
  baseY: number;
  baseZ: number;
  size: number;
  speed: number;
  phase: number;
  brightness: number;
};

const PARTICLE_Y_RANGE = 8.5;
const PARTICLE_Y_MIN = -3.8;

const vertexShader = /* glsl */ `
  attribute float particleSize;
  attribute float particleBrightness;

  uniform float sizeScale;
  uniform float opacity;

  varying float vBrightness;
  varying float vDepthFade;

  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float perspectiveScale = 260.0 / max(-mvPosition.z, 0.001);
    gl_PointSize = particleSize * sizeScale * perspectiveScale;
    gl_Position = projectionMatrix * mvPosition;

    vBrightness = particleBrightness;
    vDepthFade = smoothstep(-8.0, 2.5, position.y) * opacity;
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec3 particleColor;

  varying float vBrightness;
  varying float vDepthFade;

  void main() {
    vec2 c = gl_PointCoord - vec2(0.5);
    float dist = length(c);
    if (dist > 0.5) discard;

    float core = 1.0 - smoothstep(0.0, 0.5, dist);
    float softEdge = 1.0 - smoothstep(0.22, 0.5, dist);
    float alpha = (core * 0.32 + softEdge * 0.68) * vDepthFade * vBrightness;
    vec3 color = particleColor * (0.68 + vBrightness * 0.38);

    gl_FragColor = vec4(color, alpha);
  }
`;

const PARTICLES_THROTTLE_SECONDS = 1 / 30;

export class SuspendedParticles extends Points {
  private readonly seeds: ParticleSeed[] = [];
  private readonly positionArray: Float32Array;
  private readonly sizeUniform: Uniform<number>;
  private readonly opacityUniform: Uniform<number>;
  private settings = DEFAULT_UNDERWATER_VISUAL_SETTINGS;
  private lastUpdateTime = 0;

  constructor(roomSize: number, maxCount: number) {
    const positionArray = new Float32Array(maxCount * 3);
    const sizeArray = new Float32Array(maxCount);
    const brightnessArray = new Float32Array(maxCount);
    const geometry = new BufferGeometry();

    geometry.setAttribute("position", new BufferAttribute(positionArray, 3));
    geometry.setAttribute("particleSize", new BufferAttribute(sizeArray, 1));
    geometry.setAttribute(
      "particleBrightness",
      new BufferAttribute(brightnessArray, 1)
    );

    const sizeUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.particleSize
    );
    const opacityUniform = new Uniform(
      DEFAULT_UNDERWATER_VISUAL_SETTINGS.particleOpacity
    );
    const material = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: AdditiveBlending,
      uniforms: {
        particleColor: new Uniform(new Color("#d7fdff")),
        sizeScale: sizeUniform,
        opacity: opacityUniform,
      },
      vertexShader,
      fragmentShader,
    });

    super(geometry, material);

    this.positionArray = positionArray;
    this.sizeUniform = sizeUniform;
    this.opacityUniform = opacityUniform;
    this.name = "suspended-particles";
    this.frustumCulled = false;
    this.renderOrder = 8;

    for (let i = 0; i < maxCount; i += 1) {
      this.seeds.push({
        baseX: Math.random() * roomSize - roomSize / 2,
        baseY: Math.random() * PARTICLE_Y_RANGE + PARTICLE_Y_MIN,
        baseZ: Math.random() * roomSize - roomSize / 2,
        size: 0.012 + Math.random() * 0.028,
        speed: 0.18 + Math.random() * 0.46,
        phase: Math.random() * Math.PI * 2,
        brightness: 0.35 + Math.random() * 0.65,
      });
      sizeArray[i] = this.seeds[i].size;
      brightnessArray[i] = this.seeds[i].brightness;
    }

    this.setSettings(DEFAULT_UNDERWATER_VISUAL_SETTINGS);
  }

  setSettings(settings: UnderwaterVisualSettings) {
    this.settings = settings;
    const count = Math.max(
      0,
      Math.min(this.seeds.length, Math.round(settings.particleCount))
    );
    this.geometry.setDrawRange(0, count);
    this.visible = count > 0 && settings.particleOpacity > 0;
    this.sizeUniform.value = settings.particleSize;
    this.opacityUniform.value = settings.particleOpacity;
  }

  update(time: number) {
    if (time - this.lastUpdateTime < PARTICLES_THROTTLE_SECONDS) return;
    this.lastUpdateTime = time;

    const { particleDrift, particleSpeed, particleSpread } = this.settings;
    const count = this.geometry.drawRange.count;
    const speed = Math.max(0, particleSpeed);
    const drift = Math.max(0, particleDrift);
    const spread = Math.max(0.1, particleSpread);

    for (let i = 0; i < count; i += 1) {
      const seed = this.seeds[i];
      const i3 = i * 3;
      const yLoop =
        (seed.baseY + time * speed * seed.speed + Math.abs(PARTICLE_Y_MIN)) %
        PARTICLE_Y_RANGE;
      const wave = time * (0.22 + seed.speed * 0.24) * speed + seed.phase;

      this.positionArray[i3] =
        seed.baseX * spread +
        Math.sin(wave) * 0.16 * drift +
        Math.sin(time * 0.05 + seed.phase) * 0.08 * drift;
      this.positionArray[i3 + 1] =
        PARTICLE_Y_MIN + yLoop + Math.sin(wave * 0.63) * 0.045 * drift;
      this.positionArray[i3 + 2] =
        seed.baseZ * spread + Math.cos(wave * 0.81) * 0.16 * drift;
    }

    const position = this.geometry.attributes.position as BufferAttribute;
    position.needsUpdate = true;
  }
}
